import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Divider, Tooltip, Chip, Box, Collapse } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import Avatar from '@mui/material/Avatar';
import UserProfilePopup from './UserProfilePopup';
import { useAuth } from '../context/AuthContext';
import { useGameSpace } from '../context/GameSpaceContext';
import { useBilling } from "../context/BillingContext";
import useOnboarding from '../hooks/useOnboarding';
import { ROLE_ADMIN, ROLE_CREATOR, ROLE_PLAYER, canCreateInSpace } from '../utils/roles';

const sectionLabelSx = {
  color: 'text.secondary',
  fontWeight: 'bold',
  fontSize: '0.8rem',
};

const chipSx = {
  ml: 'auto',
  fontSize: '0.65rem',
  height: 18,
  letterSpacing: '0.02em',
  flexShrink: 0,
};

const isSettingsRoute = (pathname) =>
  pathname === '/manage-users' ||
  pathname.startsWith('/admin/') ||
  /^\/gamespaces\/[^/]+\/manage-users$/.test(pathname);

export default function SettingsMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [learnspacesExpanded, setLearnspacesExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);

  const {
    userRole,
    userName,
    currentUserId,
    isAuthenticated,
    gamespaceRole,
    logout,
  } = useAuth();
  const { gameSpaces, selectedSpaceId, setSelectedSpaceId, createGameSpace } = useGameSpace();
  const navigate = useNavigate();
  const location = useLocation();
  const { billingSummary } = useBilling();
  const { resetGettingStarted } = useOnboarding();

  const ownedGamespaces = gameSpaces.filter(gs => gs.space_owner_user_id === currentUserId);
  const sharedGamespaces = gameSpaces.filter(gs => gs.space_owner_user_id !== currentUserId);
  const selectedOwnedGamespace = ownedGamespaces.find(gs => gs.space_id === selectedSpaceId);
  const selectedGamespace = gameSpaces.find(gs => gs.space_id === selectedSpaceId);

  const isAnyGamespaceOwner = ownedGamespaces.length > 0;
  const isOrganizationAdmin = userRole === ROLE_ADMIN;
  const isOrgCreator = userRole === ROLE_CREATOR;
  const canAddLearnspace = isOrganizationAdmin || (isOrgCreator && !isAnyGamespaceOwner);

  const shouldShowManageLearnspace = Boolean(selectedOwnedGamespace);
  const shouldShowBillingAction =
    shouldShowManageLearnspace && selectedOwnedGamespace.personal_subs_required === true;

  const getLearnspaceChipLabel = (gs) => {
    if (gs.space_owner_user_id === currentUserId) return 'Owner';
    if (gs.gamespace_role_id === ROLE_CREATOR) return 'Creator';
    if (gs.gamespace_role_id === ROLE_PLAYER) return 'Player';
    return 'Player';
  };

  const isCreatorInSpace = (gs) => {
    if (!gs) return false;
    if (canCreateInSpace(gs.gamespace_role_id)) return true;
    return gs.space_owner_user_id === currentUserId;
  };
  const isCreatorAnywhere = gameSpaces.some(isCreatorInSpace);

  const showOrgAdminLinks = isOrganizationAdmin;
  const showCreatorAdminLinks = isOrganizationAdmin || isCreatorAnywhere;
  const showAdminSection = showOrgAdminLinks || showCreatorAdminLinks;
  const canManageEntityProfiles =
    isOrganizationAdmin || isCreatorInSpace(selectedGamespace);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);
  const handleProfileOpen = () => {
    setProfileOpen(true);
    handleClose();
  };
  const handleProfileClose = () => setProfileOpen(false);

  const openSettingsPage = (path) => {
    handleClose();
    navigate(path, { replace: isSettingsRoute(location.pathname) });
  };

  const handleAddSpace = async () => {
    const name = window.prompt('Enter Game Space name');
    if (name) {
      await createGameSpace(name);
    }
    handleClose();
  };

  const handleSelectSpace = (id) => {
    setSelectedSpaceId(id);
    navigate('/game-cards');
    setLearnspacesExpanded(false);
    handleClose();
  };

  const handleManageLearnspaceClick = () => {
    if (!selectedOwnedGamespace) return;
    setSelectedSpaceId(selectedOwnedGamespace.space_id);
    openSettingsPage(`/gamespaces/${selectedOwnedGamespace.space_id}/manage-users`);
  };

  const handleShowGettingStarted = () => {
    const currentIsCreator = canCreateInSpace(gamespaceRole);
    const targetSpaceId = currentIsCreator
      ? selectedSpaceId
      : gameSpaces.find(isCreatorInSpace)?.space_id;
    if (targetSpaceId && targetSpaceId !== selectedSpaceId) {
      setSelectedSpaceId(targetSpaceId);
    }
    resetGettingStarted(targetSpaceId || selectedSpaceId);
    handleClose();
    navigate('/game-cards');
  };

  const handleLogout = async () => {
    handleClose();
    await logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const getInitials = (value) => {
    if (!value) return '';
    const parts = value.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const initials = getInitials(userName);

  const renderLearnspaceItem = (gs) => (
    <MenuItem
      key={gs.space_id}
      selected={gs.space_id === selectedSpaceId}
      onClick={() => handleSelectSpace(gs.space_id)}
      sx={{ pl: 3 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        {gs.space_id === selectedSpaceId ? (
          <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
        ) : (
          <Box sx={{ width: 20, flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {gs.space_name}
        </Box>
        <Chip
          label={getLearnspaceChipLabel(gs)}
          size="small"
          color={gs.space_owner_user_id === currentUserId ? 'primary' : 'default'}
          variant="outlined"
          sx={chipSx}
        />
      </Box>
    </MenuItem>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <Tooltip title="User settings">
        <IconButton size="large" color="inherit" aria-label="user settings" onClick={handleOpen}>
          {initials ? (
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2', fontSize: '0.9rem' }} alt="User initials">
              {initials}
            </Avatar>
          ) : (
            <SettingsIcon />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          '& .MuiMenuItem-root': {
            fontSize: '0.8125rem',
            fontWeight: 400,
            letterSpacing: '0.01em',
            lineHeight: 1.45,
            minHeight: 36,
            py: 0.75,
          },
        }}
      >
        <MenuItem onClick={handleProfileOpen}>User Profile</MenuItem>
        {(canCreateInSpace(gamespaceRole) || isCreatorAnywhere) && (
          <MenuItem onClick={handleShowGettingStarted}>Show Getting Started</MenuItem>
        )}

        <Divider />

        {gameSpaces.length > 0 && [
          <MenuItem key="switch-learnspace" onClick={() => setLearnspacesExpanded((prev) => !prev)}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
              <Box
                sx={{
                  ...sectionLabelSx,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedGamespace?.space_name
                  ? `Learnspace ${selectedGamespace.space_name}`
                  : 'Learnspace'}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {learnspacesExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>
            </Box>
          </MenuItem>,
          <Collapse key="learnspace-collapse" in={learnspacesExpanded} timeout="auto" unmountOnExit>
            {ownedGamespaces.map(renderLearnspaceItem)}
            {sharedGamespaces.map(renderLearnspaceItem)}
          </Collapse>,
          <Divider key="learnspace-divider" />,
        ]}

        {shouldShowManageLearnspace && [
          <MenuItem key="manage-members" onClick={handleManageLearnspaceClick}>
            Learnspace Settings
          </MenuItem>,
          shouldShowBillingAction ? (
            <Tooltip key="billing-tooltip" title="Pricing plans coming soon" placement="right">
              <Box component="span" sx={{ display: 'block', width: '100%' }}>
                <MenuItem disabled>
                  Subscription & Billing
                  {billingSummary?.freemium_info?.is_over_limit && (
                    <Chip
                      label={`${billingSummary.freemium_info.tier}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={chipSx}
                    />
                  )}
                </MenuItem>
              </Box>
            </Tooltip>
          ) : null,
        ]}

        {canAddLearnspace && (
          <MenuItem onClick={handleAddSpace}>Add Learnspace</MenuItem>
        )}

        {canManageEntityProfiles && (
          <MenuItem onClick={() => openSettingsPage('/admin/entity-profiles')}>
            Entity Profiles
          </MenuItem>
        )}

        {showAdminSection && [
          <Divider key="admin-divider" />,
          <MenuItem key="org-settings" onClick={() => setAdminExpanded((prev) => !prev)}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
              <Box sx={{ ...sectionLabelSx, flex: 1 }}>Organization Settings</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {adminExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>
            </Box>
          </MenuItem>,
          <Collapse key="admin-collapse" in={adminExpanded} timeout="auto" unmountOnExit>
            {showOrgAdminLinks && (
              <MenuItem onClick={() => openSettingsPage('/manage-users')} sx={{ pl: 3 }}>
                Manage Organization
              </MenuItem>
            )}
            {showOrgAdminLinks && (
              <MenuItem onClick={() => openSettingsPage('/admin/learnspaces')} sx={{ pl: 3 }}>
                Manage Learnspaces
              </MenuItem>
            )}
            {showOrgAdminLinks && (
              <MenuItem onClick={() => openSettingsPage('/admin/learning-content')} sx={{ pl: 3 }}>
                Learning Content Library
              </MenuItem>
            )}
            {showCreatorAdminLinks && (
              <MenuItem onClick={() => openSettingsPage('/admin/capability-taxonomies')} sx={{ pl: 3 }}>
                Capability Taxonomies
              </MenuItem>
            )}
          </Collapse>,
        ]}

        <Divider />
        <MenuItem onClick={handleLogout}>Log Out</MenuItem>
      </Menu>

      <UserProfilePopup open={profileOpen} onClose={handleProfileClose} />
    </>
  );
}
